import { useEffect, useState } from "react";
import { Gift, Copy, Check, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { safeStorage } from "@/lib/storage";

/**
 * ReferralBanner — compact "Give $50, get $50" strip meant to sit at the
 * TOP of the Dashboard, above the case list. Complements the fuller
 * ReferralCard (bottom of Dashboard) — this banner is optimized for glance
 * value / one-tap-copy.
 *
 * Auto-hides in two cases so it never becomes noise:
 *  1. User has already invited at least one person (summary.invited_count > 0)
 *  2. User dismissed it explicitly (persisted in localStorage per user)
 */
const DISMISS_KEY = "referral-banner-dismissed";

export default function ReferralBanner() {
  const [summary, setSummary] = useState(null);
  const [copied, setCopied] = useState(false);
  const [dismissed, setDismissed] = useState(
    () => safeStorage.get(DISMISS_KEY) === "1",
  );

  useEffect(() => {
    if (dismissed) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/referrals/summary");
        if (!cancelled) setSummary(data);
      } catch (err) {
        // Referrals may not be available (e.g., attorney account).
        console.warn("[ReferralBanner] summary unavailable:", err?.response?.status || err?.message);
      }
    })();
    return () => { cancelled = true; };
  }, [dismissed]);

  if (dismissed || !summary) return null;
  // Auto-hide once the user has actually invited at least one person — at
  // that point the full ReferralCard below tracks their progress and this
  // "get started" banner is redundant.
  if ((summary.invited_count || 0) > 0) return null;

  const perRef = Math.round((summary.credit_per_referral_cents || 5000) / 100);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(summary.share_url);
      setCopied(true);
      toast.success(`Copied! Send it to a family member — you'll both get $${perRef} off.`);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error("Couldn't copy. Long-press the link in the referral section below.");
    }
  };

  const dismiss = () => {
    safeStorage.set(DISMISS_KEY, "1");
    setDismissed(true);
  };

  return (
    <div
      className="mb-6 rounded-xl p-4 sm:p-5 flex items-center gap-3 sm:gap-4 relative anim-in"
      style={{
        background: "linear-gradient(90deg, var(--action) 0%, #B84E22 100%)",
        color: "#FFFFFF",
      }}
      data-testid="referral-banner"
    >
      <div
        className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
        style={{ background: "rgba(255,255,255,0.18)" }}
      >
        <Gift className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-extrabold text-sm sm:text-base leading-snug">
          Give ${perRef}, get ${perRef} — invite a family member.
        </div>
        <div className="text-xs opacity-90 mt-0.5 hidden sm:block">
          When they make their first payment, you both get <b>${perRef} off</b> your next payment.
        </div>
      </div>
      <button
        onClick={copy}
        className="text-xs sm:text-sm font-semibold px-3 py-2 rounded-md shrink-0 flex items-center gap-1.5 hover:opacity-90 transition-opacity"
        style={{ background: "#FFFFFF", color: "var(--action)" }}
        data-testid="referral-banner-copy-btn"
      >
        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
        {copied ? "Copied!" : "Copy my link"}
      </button>
      <button
        onClick={dismiss}
        className="p-1.5 rounded hover:bg-white/10 shrink-0"
        aria-label="Dismiss"
        data-testid="referral-banner-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
